// 🔧 enteries.js
// ✅ Purpose: Admin-only view of registered cars grouped by category
// ✅ Features:
// - Group cars using `catid`
// - Filter cars by selected category
// - Display vote counts per car (via `cidCount`)
// - Highlight tied top-vote cars in red
// - Export all car data to XLSX
// - Fully documented layout for easier future maintenance

import React, { useEffect, useState } from "react";
import { db, storage } from "../../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import { ref, deleteObject } from "firebase/storage";
import { useRole } from "../../role_context";
import * as XLSX from "xlsx";
import FullPageSpinner from "../../components/FullPageSpinner";
import { useCategories } from "../../hooks/use_categories";
import { useEventContext } from "../../hooks/use_event_context";
import { logger } from '../../utils/logger';
import {
  getEventVotingType,
} from "../../utils/event_voting_type_helpers";
import {
  getVotingTypeLabel,
  allowsBestInShow,
  isJudgedVotingFamily,
} from "../../utils/voting_engine_registry";
import {
  aggregateJudgedPointsByEntry,
} from "../../utils/points_results_helpers";
import { showStatus } from "../../utils/status_helpers";
import { getEntryPath, getEntryEditPath } from "../../utils/entry_urls";
import OnlineEntryControl from "../../components/online_entry_control";
import PromotionSourceButton from "../../promotion/components/promotion_source_button";

const Entries = () => {
  const navigate = useNavigate();

  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const votingType = getEventVotingType(eventDoc);
  const votingTypeLabel = getVotingTypeLabel(votingType);
  const showBestInShow =
    allowsBestInShow(votingType);
  const isJudgedVoting =
    isJudgedVotingFamily(votingType);

  // 🔒 Role check
  const { role, roleLoading } = useRole();

  // 🧠 State
  const [cars, setCars] = useState([]);
  const [state, setState] = useState("loading");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [status, setStatus] = useState(null);
  const [pendingEntryCount, setPendingEntryCount] = useState(0);
  const [entryReceiptReferences, setEntryReceiptReferences] = useState({});
  const [allowOnlineEntries, setAllowOnlineEntries] = useState(false);
  const [savingOnlineEntries, setSavingOnlineEntries] = useState(false);

  useEffect(() => {
    setAllowOnlineEntries(eventDoc?.allowOnlineEntries === true);
  }, [eventId, eventDoc?.allowOnlineEntries]);

  const saveOnlineEntries = async () => {
    setSavingOnlineEntries(true);
    try {
      await updateDoc(doc(db, "events", eventId), {
        allowOnlineEntries,
        updatedAt: serverTimestamp(),
      });
      showStatus(setStatus, { type: "success", message: "Online entry settings saved successfully." });
    } catch (error) {
      logger.error("Failed to save online entry settings", { value: error });
      showStatus(setStatus, { type: "error", message: "Unable to save online entry settings. Please try again." });
    } finally {
      setSavingOnlineEntries(false);
    }
  };

  // 📦 Load categories using custom hook
  const { categories, loading: catsLoading } = useCategories(eventId);

  // Image loading
  const [visiblePhotoIds, setVisiblePhotoIds] = useState({});

  // best of show
  const [bestInShowCars, setBestInShowCars] = useState([]);

const totalEntries = cars.length;

const totalVotes = cars.reduce(
  (sum, car) => sum + Number(car.cidCount || 0),
  0
);
  
  // 🚀 Fetch cars and votes when admin and categories are ready
  useEffect(() => {

    logger.log("👀 useEffect - role:", { role, roleLoading }, "catsLoading:", catsLoading, "categories:", categories.length);
    logger.log("🗳 Entries voting type:", {
      votingType,
    });
    
    if (!roleLoading && !catsLoading && eventId && !eventLoading) {
      getVotesAndCars();
    }
  }, [role, roleLoading, catsLoading, eventId, eventLoading]);
    
  const toggleImage = (car) => {
    const carId = car.id;
  
    setVisiblePhotoIds((prev) => {
      const alreadyVisible = prev[carId];
      if (alreadyVisible) {
        // 👁️ Hide image
        return { ...prev, [carId]: undefined };
      }
  
      if (car.imgUrl) {
        // 📸 Show image from stored URL
        return { ...prev, [carId]: car.imgUrl };
      } else {
        // 🚫 No image available
        return { ...prev, [carId]: null };
      }
    });
  };
    
  // 📥 Load cars and votes from Firestore and compute vote counts
  const getVotesAndCars = async () => {
    logger.log("🛠️ getVotesAndCars started", {});

    try {
      logger.log("📤 Fetching votes and cars...", {});
      const [votesSnap, carsSnap, pendingSnap, approvedSnap] = await Promise.all([
        getDocs(collection(db, "events", eventId, "votes")),
        getDocs(collection(db, "events", eventId, "cars")),
        getDocs(query(collection(db, "events", eventId, "pendingEntries"), where("status", "==", "pending"))),
        getDocs(query(collection(db, "events", eventId, "pendingEntries"), where("status", "==", "approved"))),
      ]);
      setPendingEntryCount(pendingSnap.size);
      // Receipt lookup identifiers remain in manager-private submissions, never
      // copied into publicly readable cars or exposed through entry URLs.
      setEntryReceiptReferences(Object.fromEntries(approvedSnap.docs
        .map((snapshot) => snapshot.data())
        .filter((submission) => submission.approvedCarId && submission.transactionId)
        .map((submission) => [submission.approvedCarId, submission.transactionId])));
      const voteData = votesSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }));

      const judgedPointResults =
        isJudgedVoting
          ? aggregateJudgedPointsByEntry(voteData)
          : [];

      const judgedPointMap = {};
      judgedPointResults.forEach((result) => {
        judgedPointMap[result.cid] = result;
      });

      const carData = carsSnap.docs.map(doc => {

        const data = doc.data();

        return {
          ...data,

          id: doc.id,

          // ==================================================
          // SAP / Demo Engine Compatibility Mapping
          //
          // Purpose:
          // - Preserve legacy admin entry rendering
          // - Support Experience Factory generated entries
          // - Avoid Firestore migration
          // ==================================================

          cid: doc.id,

          catid:
            data.catid ||
            data.categoryId ||
            null,

          owner:
            data.owner ||
            data.ownerName ||
            "Unknown Owner",
        };

      });

      logger.log("✅ voteData loaded:", {
        value: voteData.length
      });
      logger.log("✅ carData loaded:", {
        value: carData.length
      });

      // 🧮 Count votes per car (by cid)
      const voteCounts = voteData.reduce((acc, vote) => {

        // ✅ Ranked Choice
        // Count only first-choice selections
        if (
          votingType === "ranked_choice" &&
          Array.isArray(vote.rankings) &&
          vote.rankings.length > 0
        ) {

          const firstChoice = [...vote.rankings]
            .sort((a, b) => Number(a.rank) - Number(b.rank))[0];

          if (firstChoice?.cid) {
            acc[firstChoice.cid] = (acc[firstChoice.cid] || 0) + 1;
          }

          return acc;
        }

        // ✅ Standard Voting
        if (vote.cid) {
          acc[vote.cid] = (acc[vote.cid] || 0) + 1;
        }

        return acc;

      }, {});

      logger.log("📊 voteCounts summary:", {
        value: voteCounts
      });

      // 🏷️ Attach vote count to each car
      carData.forEach(car => {
        const judgedResult = judgedPointMap[car.id];

        car.cidCount = voteCounts[car.id] || 0;

        car.averageScore = judgedResult?.averageScore || 0;
        car.averageScoreDisplay = judgedResult?.averageScoreDisplay || "0.00";
        car.judgeCount = judgedResult?.judgeCount || 0;
        car.maxBallotPoints = judgedResult?.maxBallotPoints || 100;

        car.displayMetric = isJudgedVoting
          ? car.averageScore
          : car.cidCount;
      });

      // 🏆 Count Best in Show votes by car/cid
      const bestInShowCounts = voteData
        .filter((vote) => vote.bestInShow === true && vote.cid)
        .reduce((acc, vote) => {
          acc[vote.cid] = (acc[vote.cid] || 0) + 1;
          return acc;
        }, {});

      // 🏆 Build Top 3 Best in Show cars
      const topBestInShowCars = carData
        .map((car) => ({
          ...car,
          bestInShowCount: bestInShowCounts[car.id] || 0,
        }))
        .filter((car) => car.bestInShowCount > 0)
        .sort((a, b) => b.bestInShowCount - a.bestInShowCount)
        .slice(0, 3);

      setBestInShowCars(topBestInShowCars);

      // 🟥 Highlight tied top-vote cars by updating original array
      categories.forEach(cat => {
        const carsInCat = carData.filter(c => String(c.catid) === String(cat.id));
        if (carsInCat.length === 0) return;

        const maxVotes = Math.max(...carsInCat.map(c => c.cidCount));
        const tiedCars = carsInCat.filter(c => c.cidCount === maxVotes && maxVotes > 0);

        if (tiedCars.length > 1) {
          carData.forEach(car => {
            if (
              String(car.catid) === String(cat.id) &&
              car.cidCount === maxVotes
            ) {
              car.highlightRed = true;
            }
          });
        }
      });

      setCars(carData);
      setState("loaded");

      logger.log("✅ AdminCars fully loaded", {});
    } catch (err) {
      logger.error("❌ Error loading data:", {
        value: err
      });
      setState("error");
    }
  };

  // 📤 Export car data to Excel
  const exportToExcel = () => {
    const exportData = cars.map(car => ({
      CategoryID: car.catid,
      RegID: car.regcnt,
      Description: car.description,
      Owner: car.owner,
      Votes: car.cidCount,
      CID: car.cid,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Cars");
    XLSX.writeFile(workbook, "BugRun_RegisteredCars.xlsx");
  };

  const handleDeleteCar = async (car) => {
    const category = categories.find((c) => c.id === car.catid)?.name || "Unknown Category";
    const owner = `${car.ownerFirst || ""} ${car.ownerLast || ""}`.trim();
    const confirmMsg = `🚨 Are you sure you want to delete:\n\n` +
      `Entry #${car.regcnt} — ${car.description} (${owner})\n` +
      `in category "${category}"?\n\nThis will permanently remove the item and its image.`;
  
    if (!window.confirm(confirmMsg)) return;
  
    try {
      // ✅ Delete image first if it exists
      if (car.hasImage && car.imgUrl) {
        const path = `items/${eventId}/${car.id}.jpg`; // assumes car.id is the docId
        await deleteObject(ref(storage, path));
      }
  
      // ✅ Delete Firestore doc
      await deleteDoc(doc(db, "events", eventId, "cars", car.id));
      
      // ✅ Remove from UI
      setCars((prev) =>
        prev.filter((c) => c.id !== car.id)
      );

      showStatus(setStatus, {
        type: "success",
        message: "Entry deleted successfully.",
      });

    } catch (err) {
      logger.error("❌ Error deleting car:", {
        value: err
      });
      showStatus(setStatus, {
        type: "error",
        message: "Failed to delete entry.",
      });
    }
  };

  // Wait for eventId before rendering page logic
  if (!ready || eventLoading) {
    return <FullPageSpinner message="Loading..." />;
  }

  // ⏳ Loading and role checks
  if (roleLoading || state === "loading" || catsLoading) {
    return <div className="container mt-5"><FullPageSpinner message="Loading Entries..." /></div>;
  }
  
  if (state === "error") {
    return <div className="container mt-5 text-danger">❌ Failed to load data.</div>;
  }

  logger.log("✅ categories loaded:", {
    value: categories,
  });

  // 🔍 Category list (sorted for dropdown)
  const filteredCategories = [...categories]
    .filter(
      (cat) =>
        Number(cat.id) !== 0 &&
        !cat.placeholder
    )
    .sort((a, b) => {
      const sortA = Number.isFinite(Number(a?.sortOrder))
        ? Number(a.sortOrder)
        : Number.isFinite(Number(a?.id))
        ? Number(a.id)
        : 9999;

      const sortB = Number.isFinite(Number(b?.sortOrder))
        ? Number(b.sortOrder)
        : Number.isFinite(Number(b?.id))
        ? Number(b.id)
        : 9999;

      if (sortA !== sortB) {
        return sortA - sortB;
      }

      return String(a?.name ?? "").localeCompare(
        String(b?.name ?? ""),
        undefined,
        { sensitivity: "base" }
      );
    });

  if (!eventId) return <div>❌ Event not found: {eventslug}</div>;

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1400px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">Voting Entries</h1>
            <p className="lead text-muted mb-0">
              Manage event entries, view public entry pages, print QR codes, and review voting results.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex flex-wrap gap-2 justify-content-lg-end align-items-start">
            <PromotionSourceButton source="entries" sourceScope="collection" />
            <Link to={`/${eventslug}/entry_reg`} className="btn btn-success">
              ➕ Add Entry
            </Link>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
            role="alert"
          >
            {status.message}
          </div>
        )}

        {pendingEntryCount > 0 && (
          <div className="alert alert-warning d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <strong>🕒 Pending Public Entries</strong>
              <br />
              {pendingEntryCount} public entr
              {pendingEntryCount === 1 ? "y needs" : "ies need"} manager approval.
            </div>

            <Link to={`/${eventslug}/pending_entries`} className="btn btn-warning">
              Review Pending Entries
            </Link>
          </div>
        )}

        <div className="row g-4 mb-4">
          <div className="col-12 col-md-4">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Total Entries</div>
                <div className="fs-3 fw-bold">{totalEntries}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">
                  {isJudgedVoting ? "Judged Entries" : "Total Votes"}
                </div>
                <div className="fs-3 fw-bold">
                  {isJudgedVoting
                    ? cars.filter((car) => car.judgeCount > 0).length
                    : totalVotes}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Voting Type</div>
                <div className="fs-5 fw-bold">{votingTypeLabel}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-start gap-3">
            <strong>Entry Tools</strong>
            <div className="d-flex flex-wrap gap-2">
                  <Link
                    to={`/${eventslug}/qr_print`}
                    className="btn btn-outline-primary"
                  >
                    🖨️ Print All QR
                  </Link>

                  <Link
                    to={`/${eventslug}/leaderboard_print`}
                    className="btn btn-outline-secondary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    🖨️ Quick Print Winners
                  </Link>

                  <button
                    className="btn btn-outline-success"
                    onClick={exportToExcel}
                  >
                    📤 Export XLSX
                  </button>
            </div>
          </div>
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-4">
                <label htmlFor="entry-status-filter" className="form-label">Entry Status</label>
                <select id="entry-status-filter" className="form-select" value="official"
                  onChange={(event) => { if (event.target.value === "pending") navigate(`/${eventslug}/pending_entries`); }}>
                  <option value="official">Official Entries</option>
                  <option value="pending">Pending Entries ({pendingEntryCount})</option>
                </select>
              </div>
              <div className="col-12 col-md-4">
                <label htmlFor="entry-category-filter" className="form-label">Filter by Category</label>
                <select id="entry-category-filter"
                  className="form-select"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {filteredCategories.map((cat) => (
                    <option key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              {selectedCategory !== "all" && (
                <div className="col-12 col-md-auto">
                  <button className="btn btn-outline-secondary" onClick={() => setSelectedCategory("all")}>
                    Reset Filter
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white"><strong>Online Event Entries</strong></div>
          <div className="card-body">
            <OnlineEntryControl enabled={allowOnlineEntries} onChange={setAllowOnlineEntries}
              slug={eventslug} disabled={savingOnlineEntries} />
            <button type="button" className="btn btn-primary mt-3" onClick={saveOnlineEntries}
              disabled={savingOnlineEntries}>
              {savingOnlineEntries ? "Saving..." : "Save Online Entry Settings"}
            </button>
          </div>
        </div>

        {showBestInShow && bestInShowCars.length > 0 && (
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white">
              <strong>🏆 Best in Show - Top 3</strong>
            </div>

            <div className="card-body">
              <div className="list-group">
                {bestInShowCars.map((car, index) => (
                  <div
                    key={`best-in-show-${car.id}`}
                    className="list-group-item d-flex flex-column flex-md-row justify-content-between gap-2"
                  >
                    <div>
                      <strong>#{index + 1}</strong>{" "}
                      Entry #{car.regcnt} — {car.description} by{" "}
                      {`${car.ownerFirst || ""} ${car.ownerLast || ""}`.trim() ||
                        "Unknown Owner"}
                    </div>

                    <span className="badge bg-warning text-dark rounded-pill align-self-start align-self-md-center">
                      {car.bestInShowCount} vote
                      {car.bestInShowCount !== 1 ? "s" : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {filteredCategories.map((cat) => {
          if (
            selectedCategory !== "all" &&
            String(selectedCategory) !== String(cat.id)
          ) {
            return null;
          }

          const carsInCat = cars
            .filter(
              (car) =>
                String(car.catid) === String(cat.id) && Number(car.catid) !== 0
            )
            .sort((a, b) => b.displayMetric - a.displayMetric);

          if (carsInCat.length === 0) return null;

          return (
            <div key={cat.id} className="card shadow-sm mb-4">
              <div className="card-header bg-white">
                <strong>{cat.name}</strong>
                <div className="small text-muted">
                  {carsInCat.length} entr{carsInCat.length === 1 ? "y" : "ies"}
                </div>
              </div>

              <div className="card-body">
                <div className="row g-4">
                  {carsInCat.map((car) => {
                    const ownerName =
                      `${car.ownerFirst || ""} ${car.ownerLast || ""}`.trim() ||
                      car.owner ||
                      "Unknown Owner";

                    return (
                      <div key={car.id} className="col-12 col-lg-6">
                        <div className="card h-100 border shadow-sm">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                              <div>
                                <div className="text-muted small">
                                  Entry #{car.regcnt || "N/A"}
                                </div>

                                <h5 className="mb-1">
                                  {car.description || "Untitled Entry"}
                                </h5>

                                <div className="text-muted">{ownerName}</div>
                                {entryReceiptReferences[car.id] && (
                                  <div className="small text-break mt-1">Transaction ID: {entryReceiptReferences[car.id]}</div>
                                )}
                              </div>

                              <Link
                                to={`/${eventslug}/vote_audit/${car.cid}`}
                                className={`badge rounded-pill text-decoration-none ${
                                  car.highlightRed ? "bg-danger" : "bg-primary"
                                }`}
                              >
                                {isJudgedVoting
                                  ? `${car.averageScoreDisplay}/${car.maxBallotPoints}`
                                  : `${car.cidCount} vote${
                                      car.cidCount !== 1 ? "s" : ""
                                    }`}
                              </Link>
                            </div>

                            {isJudgedVoting && (
                              <div className="small text-muted mb-3">
                                {car.judgeCount} judge
                                {car.judgeCount !== 1 ? "s" : ""} submitted
                              </div>
                            )}

                            <div className="d-flex flex-wrap gap-2">
                              <PromotionSourceButton source="entries" sourceId={car.id} className="btn btn-sm btn-outline-primary" />
                              <Link
                                to={getEntryPath(eventslug, car)}
                                className="btn btn-sm btn-outline-primary"
                              >
                                👁️ View Entry
                              </Link>

                              <Link
                                to={`/${eventslug}/qr_print/${car.entrySlug || car.id}`}
                                className="btn btn-sm btn-outline-secondary"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                🖨️ Print QR
                              </Link>

                              <Link
                                to={getEntryEditPath(eventslug, car)}
                                className="btn btn-sm btn-outline-secondary"
                              >
                                ✏️ Edit
                              </Link>

                              <Link
                                to={`/${eventslug}/vote_audit/${car.cid}`}
                                className="btn btn-sm btn-outline-secondary"
                              >
                                🔎 Audit
                              </Link>

                              {car.hasImage === true && (
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => toggleImage(car)}
                                >
                                  📷 {visiblePhotoIds[car.id] ? "Hide" : "Show"} Photo
                                </button>
                              )}

                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteCar(car)}
                              >
                                🗑️ Delete
                              </button>
                            </div>

                            {visiblePhotoIds[car.id] &&
                              visiblePhotoIds[car.id] !== null && (
                                <div className="mt-3">
                                  <img
                                    src={visiblePhotoIds[car.id]}
                                    alt="Uploaded entry"
                                    className="img-fluid rounded shadow-sm"
                                    style={{
                                      maxWidth: "100%",
                                      maxHeight: "260px",
                                      border: "1px solid #ddd",
                                      objectFit: "contain",
                                    }}
                                  />
                                </div>
                              )}

                            {visiblePhotoIds[car.id] === null && (
                              <div className="mt-3 text-muted">
                                <em>⚠️ No image uploaded.</em>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );

};

export default Entries;
